import { useEffect, useState } from "react";

/**
 * Tiny shared store for the Quick Build FAB dock/float state, mirroring the
 * SkAi dock store. The toggle pill (next to the "Add" button) and the floating
 * "+" FAB live in the same component tree, but using a module-level store keeps
 * the behaviour identical to SkAi and lets the state persist across refreshes.
 *
 *  popped   false = docked (tucked away, only the toggle pill shows),
 *           true  = floating draggable "+" button
 *  x, y     floating position (null = default bottom-right corner)
 */
const STORAGE_KEY = "quickBuildDock";

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const { popped, x, y } = JSON.parse(raw) || {};
    return { popped: !!popped, x: x ?? null, y: y ?? null };
  } catch {
    return {};
  }
};

let state = { popped: false, x: null, y: null, ...loadPersisted() };
const listeners = new Set();

const persist = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ popped: state.popped, x: state.x, y: state.y }));
  } catch {
    /* ignore quota/availability errors */
  }
};

export const quickBuildDock = {
  get: () => state,
  set: (patch) => {
    state = { ...state, ...patch };
    persist();
    listeners.forEach((l) => l(state));
  },
  subscribe: (l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useQuickBuildDock() {
  const [snap, setSnap] = useState(state);
  useEffect(() => {
    setSnap(state); // re-sync in case state changed before we subscribed
    return quickBuildDock.subscribe(setSnap);
  }, []);
  return [snap, quickBuildDock.set];
}
